import { useCallback, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { ChatSendResponse } from '@wundo/shared/chat.ts';
import { ChatDrawer, type ChatDrawerProps } from '@wundo/ui';
import { useBorrowedSession } from '@wundo/client/impersonation.ts';
import { m } from '@/paraglide/messages.js';
import { useChatAttachments } from '@/lib/chatAttachments.ts';
import { useChatPageOffer } from '@/lib/chatPageOffer.ts';
import { pageOpening } from '@/lib/chatPageOpening.ts';
import { useChatUnread } from '@/lib/chatUnread.ts';
import { useChatMessages } from '@/lib/useChatMessages.ts';
import { useChatPageOpening } from '@/lib/useChatPageOpening.ts';
import { useChatProblem } from '@/lib/useChatProblem.ts';
import { useChatSend } from '@/lib/useChatSend.ts';
import { useDrawerConversation } from '@/lib/useDrawerConversation.ts';
import { useDrawerNudge } from '@/lib/useDrawerNudge.ts';
import { ChatBody } from '@/components/ChatBody.tsx';
import { ChatHeaderActions } from '@/components/ChatHeaderActions.tsx';
import { ChatEmptyDrawer } from '@/components/ChatEmptyDrawer.tsx';
import { ChatPageQuickActions } from '@/components/ChatPageQuickActions.tsx';
import { ChatPanelFooter } from '@/components/ChatPanelFooter.tsx';
import { ChatSidebar } from '@/components/ChatSidebar.tsx';

/** Where the drawer stands and how it is put away: the drawer's own business. */
export type ChatDrawerPlacement = Pick<
  ChatDrawerProps,
  'onDismiss' | 'onExpand' | 'width' | 'onWidthChange' | 'onWidthReset' | 'stripRef'
>;

export interface ChatPanelProps {
  open: boolean;
  placement: ChatDrawerPlacement;
  userId: string | undefined;
  expanded: boolean;
  onToggleExpanded: () => void;
}

/**
 * The chat drawer, and the conversation inside it: which conversation it
 * stands on, what was read back of it and what arrives live, what the page
 * behind it opens with, and what the user says.
 */
export function ChatPanel({
  open,
  placement,
  userId,
  expanded,
  onToggleExpanded,
}: ChatPanelProps) {
  const navigate = useNavigate();
  const impersonating = useBorrowedSession();
  /*
   * What this page offers: its line, the things worth asking on it, and what to
   * say before anything has been said. The page says all of it, not the drawer
   * — the drawer has no idea what is behind it, and a stale invitation reads as
   * a mistake.
   */
  const offer = useChatPageOffer();

  const drawer = useDrawerConversation(open);
  const { conversationId, blank } = drawer;

  /*
   * Every time someone asked for the caret without saying anything: a pill that
   * hands over to the composer rather than speaking for the user. Counted
   * rather than flagged, because asking twice has to land twice.
   */
  const [caretAsked, setCaretAsked] = useState(0);
  const askCaret = useCallback(() => setCaretAsked((asked) => asked + 1), []);

  const chat = useChatMessages({ drawerOpen: open, userId, conversationId });
  /* A reply that landed while the drawer was away, still waiting to be seen. */
  const unread = useChatUnread();

  const attachments = useChatAttachments();
  /* Only files that finished uploading can be named; the composer holds the send until they have. */
  const attachmentIds = attachments.pending.flatMap((one) => one.id ?? []);

  const problem = useChatProblem({
    conversationId,
    attachmentProblem: attachments.problem,
    historyFailed: chat.history.failed,
  });

  const { open: openConversation, startNew } = drawer;
  const { forget } = chat;
  const { clearSend, admit } = problem;

  /*
   * Clears the drawer and has the next message open a conversation. Nothing is
   * created or closed until something is said, so changing your mind costs
   * nothing — the conversation you left is still the running one.
   */
  const startNewConversation = useCallback(() => {
    startNew();
    forget();
    /* A blank drawer is not the sitting anything failed to reach. */
    clearSend();
  }, [startNew, forget, clearSend]);

  /*
   * What the page has ready for a drawer with nothing in it yet: its opening
   * question, what was said in the previous conversation, or the one thing
   * worth recommending to somebody still finding their way. Never two, so the
   * drawer treats them as one thing that arrives in several shapes.
   */
  const opening = pageOpening(offer);
  const openingState = useChatPageOpening({
    opening,
    blank,
    userId,
    startNewConversation,
    openConversation,
    askCaret,
  });
  const { nudge, settle: settleNudge } = useDrawerNudge(opening, blank);

  const sending = useChatSend({
    conversationId,
    startingNew: drawer.startingNew,
    answering: openingState.answering,
    attachmentIds,
    /* A new attempt: whatever was said about the last one no longer applies. */
    onSending: clearSend,
    onSent: (response: ChatSendResponse) => {
      attachments.clear();
      chat.waitForReply(response.conversation_id);
      chat.append(response.message);
      openingState.sent();
      drawer.spokeIn(response.conversation_id);
    },
    onFailed: () => {
      problem.sendFailed();
      chat.stopWaiting();
    },
  });

  const { send } = sending;
  /* What the user wrote; dictated words and a nudge's button go out as they are. */
  const sendWritten = useCallback(
    (text: string) => {
      if (admit(text)) {
        send(text);
      }
    },
    [admit, send],
  );

  /*
   * One turn speaks at a time: while a message is on its way out or a reply is
   * on its way in, the composer is held — no typing, no files.
   */
  const held = sending.pending || chat.responding;
  const userHasSpoken =
    sending.pending || chat.messages.some((message) => message.author === 'user');

  return (
    <ChatDrawer
      open={open}
      title={m.chat_title()}
      subtitle={offer?.line}
      closeLabel={m.chat_close()}
      collapseLabel={m.chat_collapse_drawer()}
      stripLabel={m.chat_strip()}
      resizeLabel={m.chat_resize()}
      {...placement}
      unread={unread}
      unreadLabel={m.chat_unread()}
      expanded={expanded}
      /*
       * Only in full screen, which is also the only place the history is
       * reachable from: the docked column is too narrow to spend on a list.
       */
      sidebar={
        <ChatSidebar
          currentId={conversationId}
          onOpen={openConversation}
          onNew={startNewConversation}
        />
      }
      action={
        <ChatHeaderActions
          expanded={expanded}
          onToggleExpanded={onToggleExpanded}
          onNew={startNewConversation}
          isEmpty={chat.messages.length === 0}
        />
      }
    >
      <ChatBody
        /* Another conversation is another read: it opens at its own end. */
        key={conversationId ?? 'new'}
        expanded={expanded}
        messages={chat.messages}
        streaming={chat.streaming}
        typing={chat.typing}
        activitySteps={chat.activitySteps}
        activityPinned={chat.activityPinned}
        error={problem.text}
        onSuggestion={sendWritten}
        sending={sending.pending}
        hasOlder={chat.history.hasOlder}
        loadingOlder={chat.history.loadingOlder}
        onLoadOlder={chat.history.loadOlder}
        /*
         * A link Wundo wrote to one of our own pages goes there in this tab,
         * through the router: the drawer rides along with the user, and the
         * conversation they are reading survives the move.
         */
        onNavigate={(path) => void navigate({ href: path })}
        empty={
          <ChatEmptyDrawer
            opening={openingState.answering}
            nudge={nudge}
            userId={userId}
            disabled={held || impersonating}
            onSend={sending.send}
            onNudgeSettled={settleNudge}
          />
        }
      />
      <ChatPageQuickActions
        actions={offer?.quickActions ?? []}
        userHasSpoken={userHasSpoken}
        expanded={expanded}
        disabled={held || impersonating}
        onSend={sendWritten}
        onFocus={askCaret}
        onOpening={openingState.goToOpening}
      />
      <ChatPanelFooter
        expanded={expanded}
        onSend={sendWritten}
        onDictated={sending.send}
        onDictationProblem={problem.setDictationProblem}
        disabled={held}
        impersonating={impersonating}
        /*
         * Every moment someone is about to write: the drawer opening, a
         * conversation started, one picked up out of the history — and a pill
         * that handed the turn straight over.
         */
        focusOn={`${open}:${conversationId ?? 'new'}:${caretAsked}`}
        attention={openingState.attention}
        attachments={attachments}
      />
    </ChatDrawer>
  );
}
